import DismissButton from 'components/dismiss-button'
import { ConfirmationOverlay } from 'components/confirmation-overlay'
import { useGetArchivedClassesQuery, useUnarchiveClassMutation } from 'util/rtk-api'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Class } from 'types'
import styles from 'css/components/classrooms/archived-classes-overlay.module.css'

interface ArchivedClassesOverlayProps {
    onDismiss: () => void
}

const formatDate = (ts?: number) => {
    if (!ts) return '-'
    const d = new Date(ts)
    const [, month, day, year] = d.toDateString().split(' ')
    return `${month} ${day}, ${year}`
}

export const ArchivedClassesOverlay = ({ onDismiss }: ArchivedClassesOverlayProps) => {
    const { data, isLoading } = useGetArchivedClassesQuery()
    const [unarchiveClass, { isLoading: isUnarchiving }] = useUnarchiveClassMutation()
    const [pendingUnarchive, setPendingUnarchive] = useState<{ classId: string; name: string } | null>(null)
    const navigate = useNavigate()

    const classes: Class[] = [...(data?.classes || [])].sort((a, b) =>
        ((b as any).archivedAt ?? 0) - ((a as any).archivedAt ?? 0)
    )

    const handleConfirmUnarchive = async () => {
        if (!pendingUnarchive) return
        await unarchiveClass({ classId: pendingUnarchive.classId })
        setPendingUnarchive(null)
        onDismiss()
        navigate('/classrooms')
    }

    return (
        <>
            <div className="overlay">
                <div className="overlay-content-container" style={{ minWidth: '640px' }}>
                    <div className="overlay-content-header">
                        <h3>Archived Classes</h3>
                        <DismissButton onClick={onDismiss} />
                    </div>
                    <div className="overlay-content-body">
                        {isLoading ? (
                            <p>Loading...</p>
                        ) : classes.length === 0 ? (
                            <p style={{ color: 'var(--blue-gray)', textAlign: 'center', padding: '1.5rem 0' }}>No archived classes.</p>
                        ) : (
                            <div className={styles.tableWrapper}>
                                <table className={styles.archivedTable}>
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Subject</th>
                                            <th>Grade</th>
                                            <th>Period</th>
                                            <th>Archived On</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {classes.map((c) => (
                                            <tr key={c.classId}>
                                                <td>{c.name}</td>
                                                <td>{c.subject}</td>
                                                <td>{c.grades?.join(', ')}</td>
                                                <td>{c.period}</td>
                                                <td>{formatDate((c as any).archivedAt)}</td>
                                                <td>
                                                    <button
                                                        className={styles.unarchiveBtn}
                                                        onClick={() => setPendingUnarchive({ classId: c.classId, name: c.name })}
                                                    >
                                                        Unarchive
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {pendingUnarchive && (
                <ConfirmationOverlay
                    title="Unarchive Class"
                    message={`Are you sure you want to unarchive "${pendingUnarchive.name}"? It will reappear on your classrooms page.`}
                    confirmLabel="Unarchive"
                    onConfirm={handleConfirmUnarchive}
                    onDismiss={() => setPendingUnarchive(null)}
                    isLoading={isUnarchiving}
                />
            )}
        </>
    )
}
